import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Box, useMediaQuery } from '@mui/material';

// ---------------------------------------------------------------------------
// HootDex header - the floating mint "tube" nav used on hootdex.com, so the
// exchange and the marketing site read as one product.
//
//   Desktop (>= 1400px): logo, seven menu items with mega dropdowns (hover or
//   click / keyboard), and the wallet button, all inside the pill.
//   Narrower: logo + wallet button + hamburger; the same menus open as an
//   accordion panel under the pill.
//
// Links into this app use the router (no reload); everything else opens the
// marketing / education / wallet sites exactly as hootdex.com does.
// ---------------------------------------------------------------------------

const C = {
  mint: '#D4FFF0',
  forest: '#064E3B',
  forestHover: '#047857',
  board: 'rgba(17, 24, 39, 0.98)',
  border: 'rgba(255, 255, 255, 0.08)',
  link: '#E5E7EB',
  muted: '#9CA3AF'
};
// Same stack as hootdex.com. Gotham isn't actually served there, so it renders
// in the OS UI font (SF Pro on Mac, Segoe UI on Windows) - match that exactly.
const FONT = "Gotham, -apple-system, BlinkMacSystemFont, system-ui, 'Segoe UI', sans-serif";

// Pecu Wallet sign-up (same target as the old header's "Create Pecu Wallet").
const CREATE_WALLET_URL = 'https://www.pecunovus.net/signup';

// `to` = route in this app, `href` = external, `action` = handled in Nav.
const MENU = [
  {
    label: 'Markets',
    columns: [
      {
        cap: 'Available Markets',
        links: [
          { label: 'SynthCrypto Markets', to: '/' },
          { label: 'Hybrid Token Markets', to: '/hybrid' },
          { label: 'EquiTrack Markets', to: '/equitrackTokens' },
          { label: 'XMG Token Markets', to: '/xmgTokens' },
          { label: 'DBT Markets', to: '/dbt' },
          { label: 'DCN Markets', to: '/dcn' }
        ]
      },
      {
        cap: 'More Markets',
        links: [
          { label: 'VentureToken Markets', to: '/VentureToken' },
          { label: 'Nexus Token Markets', to: '/nexus' },
          { label: 'Wrapped Token Markets', to: '/wrapTokens' },
          { label: 'Delisted Tokens', to: '/DelistedToken' }
        ]
      },
      {
        cap: 'My HootDex',
        links: [
          { label: 'My Dashboard', to: '/dashboard' },
          { label: 'Get PECU', action: 'getPecu' }
        ]
      },
      {
        cap: 'HootDex Exchange & Market Intelligence',
        text:
          'Get real-time pricing and trade various tokens on HootDex, access the high fidelity data available for each token, access digital asset liquidity, institutional order logs, and custom asset tokenization metrics built on the Pecu Novus architecture.'
      }
    ]
  },
  {
    label: 'Getting Started',
    columns: [
      {
        cap: 'User Guides',
        links: [
          { label: 'Getting Started Guide', href: 'https://www.hootdex.com/getting-started/' },
          { label: 'Self-Custody of Digital Assets', href: 'https://www.hootdex.com/self-custody-of-digital-assets/' },
          { label: 'Cost Effective, Competitive & Transparent', href: 'https://www.hootdex.com/cost-effective-competitive-and-transparent/' },
          { label: 'Institutional Digital Asset Traders', href: 'https://www.hootdex.com/institutional-digital-asset-traders/' }
        ]
      },
      {
        cap: 'Infrastructure Layers',
        links: [
          { label: 'Innovative Market Features', href: 'https://www.hootdex.com/innovative-market-features/' },
          { label: 'The MVault', href: 'https://www.hootdex.com/the-mvault/' },
          { label: 'Peer to Peer Swapping', href: 'https://www.hootdex.com/peer-to-peer-swapping/' },
          { label: 'Ecosystem', href: 'https://www.hootdex.com/ecosystem/' }
        ]
      },
      {
        cap: 'Developer Environment',
        links: [
          { label: 'Blockchain Enabled Encryption', href: 'https://www.hootdex.com/blockchain-enabled-encryption/' },
          { label: 'Industry Leading Technology', href: 'https://www.hootdex.com/industry-leading-technology/' },
          { label: 'HootDex Developer Platform', href: 'https://www.hootdex.com/hootdex-developer-platform/' },
          { label: 'NFT Fractionalization & Bundling', href: 'https://www.hootdex.com/nfts/' }
        ]
      }
    ]
  },
  {
    label: 'Tokens',
    columns: [
      {
        cap: 'Core Assets',
        links: [
          { label: 'Digital Asset Classes', href: 'https://www.hootdex.com/tokens/' },
          { label: 'Token List', href: 'https://education.hootdex.com/docs-category/all-listings/' },
          { label: 'XMG Stablecoin Series', href: 'https://www.hootdex.com/xmg-stablecoin-series' },
          { label: 'ComTrack Token Series', href: 'https://www.hootdex.com/tokens/comtrack-tokens' },
          { label: 'SynthCrypto Token Series', href: 'https://www.hootdex.com/tokens/synthcrypto/' },
          { label: 'EquiTrack Token Series', href: 'https://www.hootdex.com/tokens/equitrack-tokens' },
          { label: 'EquiTrack Token Baskets', href: 'https://www.hootdex.com/tokens/ettbaskets/' },
          { label: 'Hybrid Token Series', href: 'https://www.hootdex.com/tokens/cryptopairs/' }
        ]
      },
      {
        cap: 'Digital Credit Notes & Perpetuals',
        links: [
          { label: 'HootDex Perpetual Markets', href: 'https://www.hootdex.com/hootdex-perpetual-markets/' },
          { label: 'Multiplier Perpetual Tokens', href: 'https://www.hootdex.com/tokens/multiplier-perpetual-tokens/' },
          { label: 'HootDex Prediction Markets', href: 'https://www.hootdex.com/hootdex-prediction-markets/' },
          { label: 'Digital Credit Note "DCN"', href: 'https://www.hootdex.com/tokens/digital-credit-note-tokens/' },
          { label: 'What are DCN tokens?', href: 'https://www.hootdex.com/what-are-digital-credit-note-tokens/' }
        ]
      },
      {
        cap: 'Yield & DBT Tokens',
        links: [
          { label: 'XMG Prime Yield Tokens – XPYT', href: 'https://www.hootdex.com/tokens/xmg-prime-yield-tokens/' },
          { label: 'XMG Apex Yield Tokens – XAYT', href: 'https://www.hootdex.com/tokens/xmg-apex-yield-tokens/' },
          { label: 'Digital Basket Token "DBT"', href: 'https://www.hootdex.com/tokens/digital-basket-token-dbt/' },
          { label: 'DBT In-Kind Redemptions', href: 'https://www.hootdex.com/dbt-inkind-redemptions/' }
        ]
      },
      {
        cap: 'Specialty Profiles',
        links: [
          { label: 'Venture Tokens', href: 'https://www.hootdex.com/tokens/venture-tokens/' },
          { label: 'Composite Asset Tokens – CATs', href: 'https://www.hootdex.com/tokens/composite-asset-tokens/' },
          { label: 'Multi-Asset Digital Treasury', href: 'https://www.hootdex.com/multi-asset-digital-asset-treasury/' },
          { label: 'Fractional Ownership – FOT', href: 'https://www.hootdex.com/tokens/fractional-ownership-tokens-fot-tokens/' },
          { label: 'Wrapped Tokens', href: 'https://www.hootdex.com/tokens/wrapped-tokens/' },
          { label: 'Nexus Tokens', href: 'https://www.hootdex.com/tokens/nexus-tokens' }
        ]
      }
    ]
  },
  {
    label: 'Support',
    columns: [
      {
        cap: 'Support',
        links: [
          { label: 'HootDex Onboarding', href: 'https://www.hootdex.com/hootdex-onboarding/' },
          { label: 'Pecu Wallet + MetaMask', href: 'https://www.hootdex.com/pecu-wallet-metamask-wallet' },
          { label: 'HootDex Blind OTC Desk', href: 'https://www.hootdex.com/hootdex-blind-otc-desk' },
          { label: 'mChatHive Connect', href: 'https://mchathive.com/profile/hootdex' },
          { label: 'News and Articles', href: 'https://education.hootdex.com/docs-category/news-articles/' },
          { label: 'Frequently Asked Questions', href: 'https://education.hootdex.com/docs-category/faq-answered/' }
        ]
      },
      {
        cap: 'Announcements',
        links: [
          { label: 'New Listings', href: 'https://education.hootdex.com/docs/new-listings-on-hootdex-overview/' },
          { label: 'Delistings Hub', href: 'https://education.hootdex.com/docs/tokens-delisted-from-hootdex-overview/' },
          { label: 'All Delistings Archive', to: '/DelistedToken' },
          { label: 'Maintenance Updates', href: 'https://education.hootdex.com/docs-category/maintenance-updates/' }
        ]
      },
      {
        cap: 'Support Overview',
        text:
          'Support is a part of the Education Center and for institutions they will be able to get direct assistance with institutional desk onboarding or liquidity parameters, For the most up to date information please connect safely via mChatHive to stay up to date.'
      }
    ]
  },
  {
    label: 'Education',
    columns: [
      {
        cap: 'Knowledge Base',
        links: [
          { label: 'Education & Support Center', href: 'https://education.hootdex.com/' },
          { label: 'Digital Assets Glossary', href: 'https://www.hootdex.com/digital-assets-glossary/' },
          { label: 'Investment Terms Glossary', href: 'https://www.hootdex.com/investment-terms-glossary/' },
          { label: 'Commodities Glossary', href: 'https://www.hootdex.com/commodities-glossary/' },
          { label: 'Latest News Headlines', href: 'https://www.hootdex.com/latest-news' }
        ]
      },
      {
        cap: 'HootDex Education Center',
        text:
          'Learn more about HootDex, blockchain technology, architectural intricacies governing protocols, digital asset & credit structures, and Decentralised order books through detailed whitepapers and documentation.'
      }
    ]
  },
  {
    label: 'Architecture',
    columns: [
      {
        cap: 'Protocol Specifications',
        links: [
          { label: 'Decentralised CLOB', href: 'https://www.hootdex.com/central-limit-order-book/' },
          { label: 'HootDex + Pecu Novus', href: 'https://www.hootdex.com/hootdex-and-pecu-novus' },
          { label: 'Pecu Novus Economics', href: 'https://www.hootdex.com/pecu-novus-economic-architecture' },
          { label: 'Pricing and Oracle Model', href: 'https://www.hootdex.com/hootdex-pricing-and-oracle-architecture/' },
          { label: 'HootDex Vault Liquidity & Fees', href: 'https://www.hootdex.com/hootdex-architecture-tvl-fees/' },
          { label: 'ERC-20 Interface', href: 'https://www.hootdex.com/hootdex-erc20-listings/' },
          { label: 'Trading Fees Matrix', href: 'https://www.hootdex.com/hootdex-trading-fees/' },
          { label: 'Composite Pricing Engine', href: 'https://www.hootdex.com/composite-pricing-engine' }
        ]
      },
      {
        cap: 'Engine Specifications',
        text:
          'HootDex functions on a high-fidelity algorithmic match clearing layer known as a Decentralised Central Limit Order Book, maximizing network transaction bandwidth while ensuring fully non-custodial Decentralised security frameworks.'
      }
    ]
  },
  {
    label: 'Pecu Wallet',
    columns: [
      {
        cap: 'Network Links',
        links: [
          { label: 'Create Pecu Wallet', href: CREATE_WALLET_URL },
          { label: 'Get Your Wallet', href: 'https://www.pecunovus.net/' },
          { label: 'Get PECU', action: 'getPecu' },
          { label: 'Pecu Wallet + MetaMask', href: 'https://www.hootdex.com/pecu-wallet-metamask-wallet' },
          { label: 'Pecu Novus Blockchain', href: 'https://www.pecunovus.com/' },
          { label: 'Pecu Blockchain Explorer', href: 'https://www.pecuscan.com/' },
          { label: 'Github Repository', href: 'https://github.com/MegaHoot/pecunovus' }
        ]
      },
      {
        cap: 'Pecu Wallet & Explorer Access',
        text:
          'These links will bring you to your Pecu Wallet, Pecuscan and the Github repository for Pecu Novus, so you can Interact safely with the primary open-source transaction ledger.'
      }
    ]
  }
];

const capStyle = {
  fontFamily: FONT,
  color: C.mint,
  fontSize: '0.8rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  mb: '12px',
  pb: '6px',
  borderBottom: '1px solid rgba(212,255,240,0.15)'
};

const linkSx = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  background: 'none',
  border: 0,
  cursor: 'pointer',
  fontFamily: FONT,
  color: C.link,
  // components/nav/style.css has a global `a { font-weight: 900; font-size:
  // 1.8rem }`; pin both so every menu item matches hootdex.com.
  fontWeight: '400 !important',
  fontSize: '0.84rem !important',
  lineHeight: 1.4,
  textDecoration: 'none',
  py: '8px',
  px: '10px',
  mx: '-10px',
  borderRadius: '6px',
  transition: 'background 0.15s, color 0.15s',
  '&:hover, &:focus-visible': {
    background: 'rgba(255,255,255,0.05)',
    color: C.mint,
    outline: 'none'
  }
};

function MenuLink({ link, onAction, onNavigate }) {
  if (link.action) {
    return (
      <Box
        component="button"
        type="button"
        sx={linkSx}
        onClick={() => {
          onAction?.(link.action);
          onNavigate?.();
        }}
      >
        {link.label}
      </Box>
    );
  }
  if (link.to) {
    return (
      <Box component={Link} to={link.to} sx={linkSx} onClick={onNavigate}>
        {link.label}
      </Box>
    );
  }
  return (
    <Box
      component="a"
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
      sx={linkSx}
      onClick={onNavigate}
    >
      {link.label}
    </Box>
  );
}

function Columns({ section, onAction, onNavigate, compact }) {
  return section.columns.map((col) => (
    <Box key={col.cap} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Box sx={capStyle}>{col.cap}</Box>
      {col.text ? (
        <Box
          sx={{
            fontFamily: FONT,
            color: C.muted,
            fontSize: '0.82rem',
            lineHeight: 1.5,
            display: compact ? 'none' : 'block'
          }}
        >
          {col.text}
        </Box>
      ) : (
        col.links.map((l) => (
          <MenuLink
            key={l.label}
            link={l}
            onAction={onAction}
            onNavigate={onNavigate}
          />
        ))
      )}
    </Box>
  ));
}

function WalletButton({ connected, onConnect, onDisconnect, small }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={connected ? onDisconnect : onConnect}
      sx={{
        fontFamily: FONT,
        flexShrink: 0,
        cursor: 'pointer',
        border: `1.5px solid ${C.forest}`,
        background: connected ? 'transparent' : C.forest,
        color: connected ? C.forest : C.mint,
        borderRadius: '9999px',
        px: small ? '14px' : '20px',
        height: small ? 36 : 42,
        fontSize: small ? '0.72rem' : '0.8rem',
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        transition: 'background 0.15s, color 0.15s',
        '&:hover': connected
          ? { background: 'rgba(6,78,59,0.08)' }
          : { background: C.forestHover }
      }}
    >
      {connected ? 'Disconnect' : 'Connect Wallet'}
    </Box>
  );
}

function CreateWalletButton({ small, fullWidth }) {
  return (
    <Box
      component="a"
      href={CREATE_WALLET_URL}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        fontFamily: FONT,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: fullWidth ? '100%' : 'auto',
        boxSizing: 'border-box',
        border: `1.5px solid ${fullWidth ? C.mint : C.forest}`,
        background: 'transparent',
        color: `${fullWidth ? C.mint : C.forest} !important`,
        borderRadius: '9999px',
        px: small ? '14px' : '20px',
        height: small ? 36 : 42,
        fontSize: `${small ? '0.72rem' : '0.8rem'} !important`,
        fontWeight: '600 !important',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        transition: 'background 0.15s',
        '&:hover': {
          background: fullWidth
            ? 'rgba(212,255,240,0.08)'
            : 'rgba(6,78,59,0.08)'
        }
      }}
    >
      Create Wallet
    </Box>
  );
}

export default function HootdexHeader({
  walletConnected,
  onConnect,
  onDisconnect,
  onAction
}) {
  const isDesktop = useMediaQuery('(min-width:1400px)');
  const location = useLocation();
  const [openIdx, setOpenIdx] = useState(null); // desktop mega menu
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const closeTimer = useRef(null);
  const rootRef = useRef(null);

  const closeAll = () => {
    clearTimeout(closeTimer.current);
    setOpenIdx(null);
    setMobileOpen(false);
  };

  // Close on navigation, Escape, and outside click.
  useEffect(closeAll, [location.pathname]);
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && closeAll();
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) closeAll();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      clearTimeout(closeTimer.current);
    };
  }, []);

  // Small close delay so moving the mouse from a menu item into its panel
  // doesn't snap it shut (same idea as the "landing bridge" on hootdex.com).
  const openMenu = (i) => {
    clearTimeout(closeTimer.current);
    setOpenIdx(i);
  };
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenIdx(null), 180);
  };

  const active = openIdx !== null ? MENU[openIdx] : null;

  return (
    <Box
      ref={rootRef}
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1200,
        px: { xs: '10px', sm: '20px' },
        py: { xs: '10px', sm: '15px' }
      }}
    >
      <Box
        component="nav"
        aria-label="HootDex"
        sx={{
          position: 'relative',
          maxWidth: 1360,
          height: { xs: 60, md: 70 },
          mx: 'auto',
          background: C.mint,
          borderRadius: '9999px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          pl: '15px',
          pr: { xs: '12px', md: '18px' },
          boxShadow: '0 12px 30px rgba(6, 78, 59, 0.06)'
        }}
      >
        <Link
          to="/"
          aria-label="HootDex home"
          style={{ display: 'flex', flexShrink: 0 }}
        >
          <Box
            component="img"
            src="/favicon.png"
            alt="HootDex"
            sx={{ width: { xs: 42, md: 50 }, height: { xs: 42, md: 50 }, objectFit: 'contain' }}
          />
        </Link>

        {isDesktop ? (
          <>
            <Box
              component="ul"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                listStyle: 'none',
                m: 0,
                p: 0,
                ml: 'auto'
              }}
            >
              {MENU.map((section, i) => (
                <Box
                  component="li"
                  key={section.label}
                  onMouseEnter={() => openMenu(i)}
                  onMouseLeave={scheduleClose}
                >
                  <Box
                    component="button"
                    type="button"
                    aria-expanded={openIdx === i}
                    aria-haspopup="true"
                    onClick={() => setOpenIdx(openIdx === i ? null : i)}
                    onFocus={() => openMenu(i)}
                    sx={{
                      fontFamily: FONT,
                      background: 'none',
                      border: 0,
                      cursor: 'pointer',
                      color: openIdx === i ? C.forestHover : C.forest,
                      fontWeight: 500,
                      fontSize: '0.95rem',
                      letterSpacing: '0.03em',
                      textTransform: 'uppercase',
                      py: '24px',
                      px: '10px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      whiteSpace: 'nowrap',
                      transition: 'color 0.15s',
                      '&:hover': { color: C.forestHover }
                    }}
                  >
                    {section.label}
                    <Box
                      component="span"
                      aria-hidden="true"
                      sx={{
                        fontSize: '0.55rem',
                        opacity: 0.7,
                        transition: 'transform 0.15s',
                        transform: openIdx === i ? 'rotate(180deg)' : 'none'
                      }}
                    >
                      ▼
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ ml: 1.5, display: 'flex', gap: '8px' }}>
              {!walletConnected && <CreateWalletButton small />}
              <WalletButton
                small
                connected={walletConnected}
                onConnect={onConnect}
                onDisconnect={onDisconnect}
              />
            </Box>

            {active && (
              <Box
                role="menu"
                onMouseEnter={() => clearTimeout(closeTimer.current)}
                onMouseLeave={scheduleClose}
                sx={{
                  position: 'absolute',
                  top: 'calc(100% + 10px)',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 'min(1100px, calc(100vw - 40px))',
                  background: C.board,
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${C.border}`,
                  borderRadius: '28px',
                  boxShadow: '0 40px 80px rgba(0,0,0,0.45)',
                  p: '30px',
                  display: 'grid',
                  gridTemplateColumns: `repeat(${Math.max(
                    active.columns.length,
                    2
                  )}, minmax(0, 1fr))`,
                  gap: '24px',
                  // invisible bridge over the 10px gap
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -12,
                    left: 0,
                    right: 0,
                    height: 12
                  }
                }}
              >
                <Columns
                  section={active}
                  onAction={onAction}
                  onNavigate={closeAll}
                />
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!walletConnected && (
              <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                <CreateWalletButton small />
              </Box>
            )}
            <WalletButton
              small
              connected={walletConnected}
              onConnect={onConnect}
              onDisconnect={onDisconnect}
            />
            <Box
              component="button"
              type="button"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
              sx={{
                width: 42,
                height: 42,
                border: 0,
                borderRadius: '50%',
                background: mobileOpen ? 'rgba(6,78,59,0.1)' : 'transparent',
                color: C.forest,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22
              }}
            >
              {mobileOpen ? '✕' : '☰'}
            </Box>
          </Box>
        )}
      </Box>

      {!isDesktop && mobileOpen && (
        <Box
          sx={{
            maxWidth: 1360,
            mx: 'auto',
            mt: '10px',
            background: C.board,
            border: `1px solid ${C.border}`,
            borderRadius: '24px',
            boxShadow: '0 30px 60px rgba(0,0,0,0.45)',
            maxHeight: 'calc(100vh - 110px)',
            overflowY: 'auto',
            p: '10px 18px'
          }}
        >
          {!walletConnected && (
            <Box sx={{ display: { xs: 'block', sm: 'none' }, pt: '10px', pb: '6px' }}>
              <CreateWalletButton small fullWidth />
            </Box>
          )}
          {MENU.map((section, i) => {
            const open = mobileSection === i;
            return (
              <Box
                key={section.label}
                sx={{ borderBottom: i < MENU.length - 1 ? `1px solid ${C.border}` : 0 }}
              >
                <Box
                  component="button"
                  type="button"
                  aria-expanded={open}
                  onClick={() => setMobileSection(open ? null : i)}
                  sx={{
                    width: '100%',
                    background: 'none',
                    border: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: '16px',
                    fontFamily: FONT,
                    color: open ? C.mint : C.link,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase'
                  }}
                >
                  {section.label}
                  <Box
                    component="span"
                    sx={{
                      fontSize: '0.6rem',
                      transition: 'transform 0.15s',
                      transform: open ? 'rotate(180deg)' : 'none'
                    }}
                  >
                    ▼
                  </Box>
                </Box>
                {open && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: '18px',
                      pb: '18px'
                    }}
                  >
                    <Columns
                      compact
                      section={{
                        ...section,
                        columns: section.columns.filter((c) => !c.text)
                      }}
                      onAction={onAction}
                      onNavigate={closeAll}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
